import React from "react";
import axiosInstance from "@/api/axiosInstance";
import { ENDPOINTS } from "@/constants/api";
import { Competition } from "@/common/model/Competition";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/Dialog";
import { Button } from "@/components/ui";

interface DeleteCompetitionDialogProps {
  open: boolean;
  onClose: () => void;
  competition: Competition | null;
  onSuccess: () => void;
}

const DeleteCompetitionDialog: React.FC<DeleteCompetitionDialogProps> = ({
  open,
  onClose,
  competition,
  onSuccess,
}) => {
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleDelete = async () => {
    if (!competition) return;
    
    setIsDeleting(true);
    setError(null);
    
    try {
      await axiosInstance.delete(`${ENDPOINTS.COMPETITIONS.DEFAULT}/event/${competition.id}`);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Error deleting competition:", err);
      setError(
        err.response?.data || 
        "Failed to delete competition. It may contain heats or users that need to be removed first."
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Competition</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {competition && (
            <p className="text-gray-700">
              Are you sure you want to delete <span className="font-bold">{competition.name}</span>?
            </p>
          )}
          <p className="text-gray-600 mt-2 text-sm">
            This action cannot be undone. The competition can only be deleted if it has no heats or users.
          </p>
          {error && (
            <div className="mt-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
              {error}
            </div>
          )}
        </div>
        <DialogFooter className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="delete"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteCompetitionDialog;
